/**
 * ================================================================================================
 *  מדיניות אימות דו־שלבי — the write shipped in X5 and had no screen for a week
 * ================================================================================================
 *
 * `tenant.security.mfa_policy.set` has existed since X5/R-7 and `reachability.test.ts` has carried
 * it as an unreached route since, with the reason written down: *"the write path shipped and the
 * panel it belongs on needs Hebrew the Master does not define."*
 *
 * ── THE READ CAME FIRST, AND IT HAD TO ────────────────────────────────────────────────────────
 *
 * A panel that can only WRITE is the `a-state-printed-as-a-literal` defect waiting to happen: it
 * offers three options and cannot say which is in force, so it either shows nothing or picks one.
 * Picking one would be exactly what migration 0012 removed the column DEFAULT to prevent —
 * *"a policy nobody chose, applied to every new customer, invisible in review"* — moved from the
 * schema into a React component.
 *
 * So `tenant.security.mfa_policy.read` was built with this panel, under `tenant.settings.read`:
 * seeing which policy is in force is not the authority to change it.
 *
 * ── `policy: null` IS A MISSING DATUM, NOT `optional` ─────────────────────────────────────────
 *
 * The server answers `null` when the row is gone or its value is one this build does not
 * recognise. The panel says so and OFFERS NO WRITE in that state: a person cannot sensibly choose
 * a replacement for a value nobody can read, and the same office's sign-in is already refusing —
 * `SqlAuthenticationStore` returns `null` for the same condition and the caller turns it into the
 * uniform sign-in refusal. Two surfaces, one answer.
 *
 * ── THE VOCABULARY IS THE MIGRATION'S, AND IS NOT RESTATED ────────────────────────────────────
 *
 * `CK_tenant_mfa_policy` admits three values and `isTenantMfaPolicy` is the one guard the writer
 * and the reader share. This file needs HEBREW for each, which is wording and not vocabulary — and
 * `POLICY_WORDS` is keyed by the three codes so a fourth added to the constraint renders as its
 * own code here rather than as a silent default.
 */

import { useCallback, useState } from 'react';

import { ActionFailureNotice } from './ActionFailureNotice';
import { Modal } from './Modal';
import { ServerRead } from './ServerRead';
import { StateBlock } from './StateBlock';
import { callOperation } from '../api/operations';
import { classifyActionError } from '../api/actionOutcome';
import type { ActionFailure } from '../api/actionOutcome';
import { useServerData } from '../api/useServerData';

/**
 * WHAT EACH POLICY MEANS, in the office's own words.
 *
 * The SECOND line of each is what an administrator actually needs: not the name of the setting but
 * who is locked out of what if they choose it. A radio list of three codes with no consequences is
 * a choice made blind, and this one can lock an office out of its own books.
 */
const POLICY_WORDS: Readonly<
  Record<string, { readonly labelHe: string; readonly meansHe: string }>
> = {
  optional: {
    labelHe: 'רשות',
    meansHe: 'כל משתמש מחליט בעצמו אם להפעיל אימות דו־שלבי. איש אינו נחסם.',
  },
  required_for_admins: {
    labelHe: 'חובה למנהלים',
    meansHe:
      'בעלי הרשאות ניהול בארגון חייבים אימות דו־שלבי. מנהל שטרם הגדיר אותו יופנה להגדרה ולא יוכל להיכנס למסכים אחרים עד שיסיים.',
  },
  required_for_all: {
    labelHe: 'חובה לכולם',
    meansHe:
      'כל משתמשי הארגון חייבים אימות דו־שלבי. מי שטרם הגדיר אותו יופנה להגדרה ולא יוכל לעבוד עד שיסיים.',
  },
};

/** The order they are offered in: least restrictive first, so the list reads as a scale. */
const POLICY_ORDER: readonly string[] = ['optional', 'required_for_admins', 'required_for_all'];

export function MfaPolicyPanel(): JSX.Element {
  const read = useCallback(() => callOperation('tenant.security.mfa_policy.read', {}), []);
  const policy = useServerData(read);

  const [choosing, setChoosing] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [failure, setFailure] = useState<ActionFailure | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  async function apply(next: string): Promise<void> {
    setSending(true);
    setFailure(null);
    try {
      await callOperation('tenant.security.mfa_policy.set', { body: { policy: next } });
      setSaved(next);
      policy.reload();
    } catch (err) {
      setFailure(classifyActionError(err));
    } finally {
      setSending(false);
      setChoosing(null);
    }
  }

  return (
    <section className="panel" data-area="mfa-policy">
      <h2 className="panel__title">מדיניות אימות דו־שלבי</h2>

      {/*
        THE ANSWER LIVES ABOVE THE READ IT INVALIDATES. `apply` reloads the policy, which returns
        `<ServerRead>` to loading; anything held inside it would be destroyed by the refresh that
        proves the write worked. The same lesson `TenantBillingScreen` records twice.
      */}
      {failure === null ? null : (
        <ActionFailureNotice failure={failure} onStepUpSatisfied={() => setFailure(null)} />
      )}
      {saved === null ? null : (
        <p className="notice__body" data-area="mfa-policy-saved">
          {`המדיניות עודכנה ל־${POLICY_WORDS[saved]?.labelHe ?? saved}. היא חלה על כניסות מעכשיו.`}
        </p>
      )}

      <ServerRead result={policy.result} whatHe="את מדיניות האימות" skeletonRows={2}>
        {(data) =>
          data.policy === null ? (
            /*
             * UNREADABLE, AND THE WRITE IS WITHHELD. Not `optional` with a warning beside it: a
             * person asked to replace a value nobody can read is being asked to guess, and the
             * office's sign-in is already refusing for the same reason.
             */
            <StateBlock
              titleHe="לא ניתן לקרוא את מדיניות האימות של הארגון"
              bodyHe={
                'השרת לא החזיר ערך שהגרסה הזאת מכירה. זה אינו אומר „רשות” — זה אומר שהמצב אינו ידוע, ' +
                'והכניסה למערכת כבר נדחית מאותה סיבה. אין לשנות מדיניות על סמך מצב שלא נקרא; יש לפנות לתמיכה.'
              }
              tone="unavailable"
            />
          ) : (
            <>
              <ul className="facts">
                {POLICY_ORDER.map((code) => {
                  const words = POLICY_WORDS[code];
                  const current = code === data.policy;
                  return (
                    <li key={code} className="facts__row">
                      <span className="facts__label">
                        {words?.labelHe ?? code}
                        {current ? ' — בתוקף' : ''}
                      </span>
                      <span className="facts__value">
                        <span>{words?.meansHe ?? ''}</span>
                        {current ? null : (
                          <button type="button" className="btn" onClick={() => setChoosing(code)}>
                            {`מעבר ל${words?.labelHe ?? code}`}
                          </button>
                        )}
                      </span>
                    </li>
                  );
                })}
              </ul>

              {/*
                A POLICY THIS BUILD HAS NO WORDING FOR IS NAMED, not hidden. `CK_tenant_mfa_policy`
                is a growing set in principle, and an office running under a fourth value must see
                its code rather than a list that silently excludes what is in force.
              */}
              {POLICY_ORDER.includes(data.policy) ? null : (
                <p className="notice__body">
                  {`המדיניות בתוקף היא „${data.policy}”, ולגרסה הזאת אין עבורה תיאור.`}
                </p>
              )}
            </>
          )
        }
      </ServerRead>

      {choosing === null ? null : (
        <Modal
          intentHe="שינוי מדיניות האימות"
          /*
           * 09 §2.2 — what happens, to whom, and what does not. The consequence is read from the
           * chosen option rather than written once: lowering the policy and raising it affect
           * different people, and one sentence for both would be wrong for one of them.
           */
          blastRadiusHe={
            `${POLICY_WORDS[choosing]?.meansHe ?? ''} ` +
            'השינוי חל על כניסות מעכשיו ואינו מנתק משתמשים שכבר מחוברים. ' +
            'הוא אינו מוחק אמצעי אימות שמישהו כבר הגדיר, ואינו משנה הרשאות.'
          }
          fields={0}
          irreversible
          confirmLabelHe={sending ? 'משנה…' : 'אישור השינוי'}
          onConfirm={() => void apply(choosing)}
          onClose={() => setChoosing(null)}
        >
          <p className="form__note">
            הפעולה נרשמת ביומן הביקורת של הארגון, יחד עם מי שביצע אותה.
          </p>
        </Modal>
      )}
    </section>
  );
}
